import React from 'react';
import { Group, Ungroup, Ellipsis, Copy, CopyPlus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface GroupActionToolbarProps {
  variant: 'group' | 'ungroup';
  onAction: () => void;
  onCopy: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  isLocked?: boolean;
}

/**
 * Floating split-button toolbar for Group/Ungroup actions.
 * Matches the Figma "Buttons Group" pattern: a primary labeled button joined
 * to an ellipsis dropdown for secondary actions (Copy, Duplicate, Delete).
 *
 * Must be rendered with `nodrag nopan` and `onMouseDown preventDefault` so
 * canvas interactions don't fire when the user clicks the toolbar.
 */
export const GroupActionToolbar: React.FC<GroupActionToolbarProps> = ({
  variant,
  onAction,
  onCopy,
  onDuplicate,
  onDelete,
  isLocked = false,
}) => {
  const label = variant === 'group' ? 'Group Selected Nodes' : 'Ungroup';
  const Icon = variant === 'group' ? Group : Ungroup;

  return (
    <div
      className='nodrag nopan flex items-stretch rounded-lg border border-border bg-card shadow-[0_4px_6px_-1px_rgba(0,0,0,0.1)]'
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
      onPointerDown={(e) => e.stopPropagation()}
    >
      {/* Primary action button */}
      <Button
        variant='ghost'
        disabled={isLocked}
        onClick={onAction}
        className='h-9 gap-2 rounded-l-lg rounded-r-none border-r border-border px-3 py-2 text-sm font-medium text-gray-700 dark:text-white'
      >
        <Icon className='size-4 shrink-0 text-gray-800 dark:text-white' />
        <span>{label}</span>
      </Button>

      {/* Ellipsis dropdown for secondary actions */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant='ghost'
            size='icon'
            disabled={isLocked}
            className='h-9 w-9 rounded-l-none rounded-r-lg text-gray-800 dark:text-white'
            aria-label='More actions'
          >
            <Ellipsis className='size-4' />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='end' className='z-[2200]'>
          <DropdownMenuItem
            disabled={isLocked}
            onClick={isLocked ? undefined : onCopy}
            className='flex items-center gap-2'
          >
            <Copy className='size-4' />
            Copy
          </DropdownMenuItem>
          <DropdownMenuItem
            disabled={isLocked}
            onClick={isLocked ? undefined : onDuplicate}
            className='flex items-center gap-2'
          >
            <CopyPlus className='size-4' />
            Duplicate
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            disabled={isLocked}
            onClick={isLocked ? undefined : onDelete}
            className='flex items-center gap-2 text-destructive focus:text-destructive'
          >
            <Trash2 className='size-4' />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
